/**
 * my_page_v3.js — DAS-107 Phase 05: MyPage V3 시안 V2 1:1 재정합
 *
 * 원본: ClaudeDesignDashboard/pages/mypage.jsx::MyPageDesktopV2 / MyPageMobileV2
 * UI: 프로필 + Key Pair 카드 그리드 (시안 V2 — 정보 구조 재설계)
 *     데스크톱 — 프로필 카드(Avatar 64×64) + KPI 2개 + 전략별 카드 2열 그리드
 *     모바일   — 프로필 카드(그래디언트+Avatar 54×54) + KPI inline + 전략별 카드 스택
 *
 * 데이터: services/apiClient (/api/exchange_key_pair/{username})
 * APIKeyEditable: hideListRender 모드 — CRUD 모달 + 토스트만 렌더
 *   - ref.openAddModal() / ref.startEdit(row) / ref.confirmDelete(id) 위임
 *   - revealedSecrets: fetchSecrets polling 패턴 (V2 검증됨)
 *
 * 시안 V2 정합 포인트:
 *   - 데스크톱: TopBar crumbs + title="My Account" + subtitle
 *   - 프로필 카드: gridColumn=span 2, Avatar(이니셜), 프로필 편집/비밀번호 변경 버튼
 *   - KPI 카드: "활성 Key Pair" (숫자 30px) + "{N} 전략 · {M} 거래소"
 *   - KPI 카드: "최근 로그인" (placeholder)
 *   - 전략 섹션 헤더: "전략 · {name}" + "{N}개 Key Pair 연결됨" + "Key 추가" 버튼
 *   - KeyPair 카드: #{id} + StatusDot + 수정/보기/삭제 버튼 + Fiat/Risky 2열 그리드
 *   - 보안 권고 배너: "90일마다 키 회전을 진행하세요."
 *   - 모바일: MobileTop title="My Account" sub=email
 *   - 모바일 프로필: 그래디언트 배경 + Avatar 54×54 + Admin · N Keys
 *   - 모바일 KPI: Keys / 전략 2칸 inline
 *
 * BE gap G5:
 *   - KeyPair status 필드 없음 → StatusDot tone="gain" placeholder
 *   - last_login 필드 없음 → "최근 사용 기록 없음" placeholder
 *   → DAS-65 G5 후속 추적
 *
 * silver 토큰 전용. JetBrains Mono 유지. raw hex 금지.
 * resize 리스너. APIKeyEditable fixed 모달 표시 보장.
 */

import React from 'react';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import APIKeyEditable from '../my_page/api_key_editable';
import { requireLoginOrThrow, getStoredUsername } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import apiClient from '../../services/apiClient';
import { StatusDot, ExchangeChip, KpiLabel, KpiValue, V2Icon, V2Badge, Btn } from '../common/claude_primitives';

/* ── 키 마스킹 표시 셀 ───────────────────────────────────── */
function KeyPill({ val }) {
    return (
        <div style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            padding: '5px 10px',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 7,
            letterSpacing: '0.05em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--color-content-secondary)',
            maxWidth: '100%',
            overflow: 'hidden',
        }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {val || '—'}
            </span>
            <V2Icon name="copy" size={11} />
        </div>
    );
}

/* ── 전략 섹션 헤더 (시안 V2 그룹 헤더) ─────────────────── */
function StrategyHeader({ name, count, onAdd }) {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
        }}>
            <div>
                <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em', color: 'var(--color-content)' }}>
                    전략 · {name}
                </div>
                <div style={{ fontSize: 11, marginTop: 2, color: 'var(--color-content-secondary)' }}>
                    {count}개 Key Pair 연결됨
                </div>
            </div>
            <Btn
                variant="outline"
                size="sm"
                icon="plus"
                onClick={onAdd}
                aria-label={`${name} 전략에 Key 추가`}
            >
                Key 추가
            </Btn>
        </div>
    );
}

/* ── 단일 KeyPair 카드 (데스크톱 2열 그리드용) ───────────── */
function KeyPairCard({ row, onEdit, onDelete, onShowSecret, revealed }) {
    const fiatExchange = row.fiat_exchange || '미설정';
    const riskyExchange = row.risky_exchange || '미설정';
    /* revealed 가 있으면 해당 필드를, 아니면 기본 마스킹값 표시 */
    const fiatApiKey = revealed?.fiat_api_key || row.fiat_api_key;
    const fiatSecretKey = revealed?.fiat_secret_key || row.fiat_secret_key;
    const riskyApiKey = revealed?.risky_api_key || row.risky_api_key;
    const riskySecretKey = revealed?.risky_secret_key || row.risky_secret_key;

    return (
        <div
            className="rounded-xl border border-surface-border bg-surface-card shadow-card"
            style={{ padding: 16 }}
        >
            {/* 카드 헤더: #{id} + StatusDot + 액션 버튼 */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 14,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 10,
                        letterSpacing: '0.1em',
                        color: 'var(--color-content-muted)',
                    }}>
                        #{row.id}
                    </span>
                    {/* TODO(DAS-65-G5): status 필드 없음 — BE 추가 후 실제 상태 표시 */}
                    <StatusDot tone="gain" size={7} label="정상" />
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                    <button
                        type="button"
                        onClick={() => onEdit(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-xs text-content-secondary hover:bg-surface-hover transition-colors"
                        aria-label="수정"
                    >
                        <V2Icon name="edit" size={10} />
                    </button>
                    <button
                        type="button"
                        onClick={() => onShowSecret(row.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-xs text-content-secondary hover:bg-surface-hover transition-colors"
                        aria-label="키 보기"
                    >
                        <V2Icon name="eye" size={10} />
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(row.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-loss/30 bg-surface-card px-2 py-1.5 text-xs text-loss hover:bg-loss-subtle transition-colors"
                        aria-label="삭제"
                    >
                        <V2Icon name="trash" size={10} />
                    </button>
                </div>
            </div>

            {/* Fiat / Risky 2열 그리드 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {/* 현물 (Fiat) */}
                <div>
                    <KpiLabel>현물 (Fiat)</KpiLabel>
                    <div style={{ marginTop: 6 }}>
                        {fiatExchange === '미설정'
                            ? <V2Badge tone="neutral">미설정</V2Badge>
                            : <ExchangeChip name={fiatExchange} />
                        }
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                        <div>
                            <div style={{
                                fontSize: 9,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'var(--color-content-muted)',
                                marginBottom: 3,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                API Key
                            </div>
                            <KeyPill val={fiatApiKey} />
                        </div>
                        <div>
                            <div style={{
                                fontSize: 9,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'var(--color-content-muted)',
                                marginBottom: 3,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                Secret
                            </div>
                            <KeyPill val={fiatSecretKey} />
                        </div>
                    </div>
                </div>

                {/* 선물 (Risky) */}
                <div>
                    <KpiLabel>선물 (Risky)</KpiLabel>
                    <div style={{ marginTop: 6 }}>
                        {riskyExchange === '미설정'
                            ? <V2Badge tone="neutral">미설정</V2Badge>
                            : <ExchangeChip name={riskyExchange} />
                        }
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                        <div>
                            <div style={{
                                fontSize: 9,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'var(--color-content-muted)',
                                marginBottom: 3,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                API Key
                            </div>
                            <KeyPill val={riskyApiKey} />
                        </div>
                        <div>
                            <div style={{
                                fontSize: 9,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'var(--color-content-muted)',
                                marginBottom: 3,
                                fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                Secret
                            </div>
                            <KeyPill val={riskySecretKey} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── 모바일 KeyPair 카드 (시안 MobileV2 카드) ────────────── */
function KeyPairCardMobile({ row, onEdit, onDelete }) {
    const fiatExchange = row.fiat_exchange || '미설정';
    const riskyExchange = row.risky_exchange || '미설정';

    return (
        <div
            className="rounded-xl border border-surface-border bg-surface-card shadow-card"
            style={{ padding: 12 }}
        >
            {/* 헤더: #{id} 거래소→거래소 StatusDot */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                flexWrap: 'wrap',
            }}>
                <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10,
                    color: 'var(--color-content-muted)',
                }}>
                    #{row.id}
                </span>
                {fiatExchange === '미설정'
                    ? <V2Badge tone="neutral">미설정</V2Badge>
                    : <ExchangeChip name={fiatExchange} />
                }
                <V2Icon name="arrowR" size={11} style={{ color: 'var(--color-content-muted)' }} />
                <ExchangeChip name={riskyExchange} />
                {/* TODO(DAS-65-G5): status 필드 없음 */}
                <StatusDot tone="gain" size={6} />
            </div>

            {/* 키 pill 행 */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <KeyPill val={row.fiat_api_key} />
                <KeyPill val={row.risky_api_key} />
            </div>

            {/* 수정 / 삭제 — 시안 MobileV2 정합 */}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                    type="button"
                    onClick={() => onEdit(row)}
                    className="flex-1 rounded-lg border border-surface-border bg-surface-card px-3 py-1.5 text-xs font-semibold text-content-secondary hover:bg-surface-hover transition-colors"
                    aria-label="수정"
                >
                    수정
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(row.id)}
                    className="rounded-lg border border-loss/30 bg-surface-card px-3 py-1.5 text-xs font-semibold text-loss hover:bg-loss-subtle transition-colors"
                    aria-label="삭제"
                >
                    삭제
                </button>
            </div>
        </div>
    );
}

/* ── 보안 권고 배너 (시안 V2 텍스트) ────────────────────── */
function SecurityNotice() {
    return (
        <div
            className="rounded-xl border bg-warning-subtle"
            style={{
                padding: 16,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                borderColor: 'color-mix(in oklch, var(--color-warning) 40%, var(--color-surface-border))',
            }}
        >
            <V2Icon name="alert" size={18} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: 1 }} />
            <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--color-content)' }}>
                    보안 권장 사항
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--color-content-secondary)' }}>
                    API Key는 IP 화이트리스트를 설정하고 출금 권한은 비활성화한 상태로 사용하시길 권장합니다.
                    90일마다 키 회전을 진행하세요.
                </div>
            </div>
        </div>
    );
}

/* ── 데스크톱 V2 (시안 MyPageDesktopV2) ──────────────────── */
function MyPageDesktopV2({ keyPairs, isLoading, onOpenAddModal, onEdit, onDelete, onShowSecret, revealedSecrets, collapsed, onCollapse }) {
    const username = getStoredUsername() || '—';

    /* 전략별 그룹화 + 거래소 unique 집계 */
    const grouped = {};
    const exchangeSet = new Set();
    (keyPairs || []).forEach((row) => {
        const sn = row.strategy_name || 'default';
        if (!grouped[sn]) grouped[sn] = [];
        grouped[sn].push(row);
        if (row.fiat_exchange) exchangeSet.add(row.fiat_exchange);
        if (row.risky_exchange) exchangeSet.add(row.risky_exchange);
    });
    const strategyNames = Object.keys(grouped).sort();
    const totalKeys = (keyPairs || []).length;
    const exchangeCount = exchangeSet.size;

    return (
        <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
            <NavBar collapsed={collapsed} onCollapse={onCollapse} />
            <main
                className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'}`}
                style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
            >
                <MaintenanceBanner />
                <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                    {/* 페이지 헤더 — 시안 TopBar crumbs + title="My Account" + subtitle */}
                    <div>
                        <div className="text-sm text-content-secondary">내 정보 / API Key 관리</div>
                        <h1 className="mt-1 text-xl font-semibold text-content">My Account</h1>
                        <div className="mt-0.5 text-sm text-content-secondary">전략 · 거래소 · API Key를 한눈에 관리</div>
                    </div>

                    {/* 프로필 + KPI 요약 그리드 — 시안: 4열, 프로필=span 2 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                        {/* 프로필 카드 — gridColumn: span 2 */}
                        <div
                            className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                            style={{ padding: 18, gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 16 }}
                        >
                            {/* Avatar 64×64 — 이니셜 2자 */}
                            <div style={{
                                width: 64,
                                height: 64,
                                borderRadius: 16,
                                background: 'linear-gradient(135deg, var(--color-surface-elevated), var(--color-surface-card))',
                                border: '1px solid var(--color-surface-border)',
                                display: 'grid',
                                placeItems: 'center',
                                fontFamily: 'JetBrains Mono, monospace',
                                fontWeight: 700,
                                fontSize: 18,
                                letterSpacing: '-0.01em',
                                color: 'var(--color-content)',
                                flexShrink: 0,
                            }}>
                                {username.slice(0, 2).toUpperCase()}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontSize: 16,
                                    fontWeight: 600,
                                    letterSpacing: '-0.01em',
                                    color: 'var(--color-content)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {username}
                                </div>
                                <div style={{
                                    fontSize: 11,
                                    marginTop: 4,
                                    color: 'var(--color-content-muted)',
                                    fontFamily: 'JetBrains Mono, monospace',
                                }}>
                                    Admin
                                </div>
                                {/* 프로필 편집 / 비밀번호 변경 버튼 — 시안 V2 정합 */}
                                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                                    <Btn variant="outline" size="sm" icon="edit" aria-label="프로필 편집">
                                        프로필 편집
                                    </Btn>
                                    <Btn variant="ghost" size="sm" icon="key" aria-label="비밀번호 변경">
                                        비밀번호 변경
                                    </Btn>
                                </div>
                            </div>
                        </div>

                        {/* 활성 Key Pair KPI */}
                        <div
                            className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                            style={{ padding: 18 }}
                        >
                            <KpiLabel>활성 Key Pair</KpiLabel>
                            <KpiValue size={30}>{isLoading ? '—' : totalKeys}</KpiValue>
                            <div style={{
                                fontSize: 11,
                                marginTop: 4,
                                color: 'var(--color-content-muted)',
                                fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                {strategyNames.length} 전략 · {exchangeCount} 거래소
                            </div>
                        </div>

                        {/* 최근 로그인 KPI — TODO(DAS-65-G5): last_login 필드 없음 */}
                        <div
                            className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                            style={{ padding: 18 }}
                        >
                            <KpiLabel>최근 로그인</KpiLabel>
                            {/* TODO(DAS-65-G5): last_login 필드 BE에 없음 — placeholder */}
                            <div style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 13,
                                fontWeight: 600,
                                marginTop: 6,
                                color: 'var(--color-content)',
                            }}>
                                최근 사용 기록 없음
                            </div>
                            <div style={{
                                fontSize: 11,
                                marginTop: 6,
                                color: 'var(--color-content-muted)',
                                fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                —
                            </div>
                        </div>
                    </div>

                    {/* 전략별 KeyPair 카드 그룹 */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <svg className="h-8 w-8 animate-spin text-brand-light" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        </div>
                    ) : (
                        <>
                            {strategyNames.map((sn) => (
                                <div key={sn}>
                                    <StrategyHeader
                                        name={sn}
                                        count={grouped[sn].length}
                                        onAdd={onOpenAddModal}
                                    />
                                    {/* 시안 V2: gridTemplateColumns repeat(2,1fr) */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                                        {grouped[sn].map((row) => (
                                            <KeyPairCard
                                                key={row.id}
                                                row={row}
                                                onEdit={onEdit}
                                                onDelete={onDelete}
                                                onShowSecret={onShowSecret}
                                                revealed={revealedSecrets?.[row.id]}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {(keyPairs || []).length === 0 && (
                                <div className="py-12 text-center text-sm text-content-muted">
                                    등록된 Key Pair가 없습니다.
                                </div>
                            )}
                        </>
                    )}

                    <SecurityNotice />
                </div>
            </main>
            <ScrollToTopButton />
        </div>
    );
}

/* ── 모바일 V2 (시안 MyPageMobileV2) ─────────────────────── */
function MyPageMobileV2({ keyPairs, isLoading, onOpenAddModal, onEdit, onDelete, collapsed, onCollapse }) {
    const username = getStoredUsername() || '—';

    const grouped = {};
    (keyPairs || []).forEach((row) => {
        const sn = row.strategy_name || 'default';
        if (!grouped[sn]) grouped[sn] = [];
        grouped[sn].push(row);
    });
    const strategyNames = Object.keys(grouped).sort();
    const totalKeys = (keyPairs || []).length;

    return (
        <div className="min-h-screen w-full bg-surface text-content">
            <NavBar collapsed={collapsed} onCollapse={onCollapse} />
            <main
                className="w-full pt-12 pb-20"
                style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
            >
                <MaintenanceBanner />
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* 페이지 헤더 — 시안 MobileTop title="My Account" sub=username */}
                    <div>
                        <h1 className="text-base font-semibold text-content">My Account</h1>
                        <div className="text-xs text-content-muted" style={{ fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
                            {username}
                        </div>
                    </div>

                    {/* 프로필 카드 — 시안 MobileV2: 그래디언트 배경 + Avatar 54×54 */}
                    <div
                        className="rounded-2xl border border-surface-border shadow-card"
                        style={{
                            padding: 18,
                            background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface-elevated))',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            {/* Avatar 54×54 */}
                            <div style={{
                                width: 54,
                                height: 54,
                                borderRadius: 14,
                                background: 'var(--color-surface-elevated)',
                                border: '1px solid var(--color-surface-border)',
                                display: 'grid',
                                placeItems: 'center',
                                fontFamily: 'JetBrains Mono, monospace',
                                fontWeight: 700,
                                fontSize: 16,
                                color: 'var(--color-content)',
                                flexShrink: 0,
                            }}>
                                {username.slice(0, 2).toUpperCase()}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: 'var(--color-content)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {username}
                                </div>
                                <div style={{
                                    fontSize: 10,
                                    marginTop: 2,
                                    color: 'var(--color-content-muted)',
                                    fontFamily: 'JetBrains Mono, monospace',
                                }}>
                                    Admin · {isLoading ? '—' : totalKeys} Keys
                                </div>
                            </div>
                            {/* 편집 아이콘 — 시안 MobileV2 정합 */}
                            <V2Icon name="edit" size={18} style={{ color: 'var(--color-content-muted)', flexShrink: 0 }} />
                        </div>
                    </div>

                    {/* KPI 행 — 시안 MobileV2: Keys / 전략 2칸 inline */}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div
                            className="rounded-xl border border-surface-border bg-surface-card shadow-card"
                            style={{ flex: 1, padding: 14 }}
                        >
                            <KpiLabel>Keys</KpiLabel>
                            <div style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontWeight: 600,
                                fontSize: 22,
                                color: 'var(--color-content)',
                            }}>
                                {isLoading ? '—' : totalKeys}
                            </div>
                        </div>
                        <div
                            className="rounded-xl border border-surface-border bg-surface-card shadow-card"
                            style={{ flex: 1, padding: 14 }}
                        >
                            <KpiLabel>전략</KpiLabel>
                            <div style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontWeight: 600,
                                fontSize: 22,
                                color: 'var(--color-content)',
                            }}>
                                {isLoading ? '—' : strategyNames.length}
                            </div>
                        </div>
                    </div>

                    {/* 전략별 목록 */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <svg className="h-6 w-6 animate-spin text-brand-light" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        </div>
                    ) : (
                        strategyNames.map((sn) => (
                            <div key={sn}>
                                {/* 전략 섹션 헤더 — 시안 MobileV2: 전략명 + "추가" pill 버튼 */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'space-between',
                                    padding: '4px 2px 8px',
                                }}>
                                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-content)' }}>
                                        {sn}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={onOpenAddModal}
                                        className="inline-flex items-center gap-1 rounded-lg border border-surface-border bg-surface-card px-2.5 py-1 text-xs text-content-secondary hover:bg-surface-hover transition-colors"
                                        aria-label={`${sn}에 키 추가`}
                                    >
                                        <V2Icon name="plus" size={10} />
                                        추가
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {grouped[sn].map((row) => (
                                        <KeyPairCardMobile
                                            key={row.id}
                                            row={row}
                                            onEdit={onEdit}
                                            onDelete={onDelete}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    )}

                    {(keyPairs || []).length === 0 && !isLoading && (
                        <div className="py-12 text-center text-sm text-content-muted">
                            등록된 Key Pair가 없습니다.
                        </div>
                    )}

                    <SecurityNotice />
                </div>
            </main>
            <ScrollToTopButton />
        </div>
    );
}

/* ── 메인 export: MyPageV3 ──────────────────────────────── */
class MyPageV3 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            listKeyPair: [],
            isLoading: true,
            /* "비밀값 보기" 토글 — rowId -> {fiat_api_key, fiat_secret_key, risky_api_key, risky_secret_key} */
            revealedSecrets: {},
            isMobile: isMobileViewport(),
        };
        /* APIKeyEditable 인스턴스 ref — 모달/편집 위임 */
        this.apiKeyRef = React.createRef();
        this._onResize = this._onResize.bind(this);
    }

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.loadKeyPairs();
        window.addEventListener('resize', this._onResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onResize);
    }

    loadKeyPairs = () => {
        const username = getStoredUsername();
        if (!username) {
            this.setState({ isLoading: false });
            return;
        }
        this.setState({ isLoading: true });
        apiClient
            .get(`/api/exchange_key_pair/${username}`)
            .then((res) => {
                this.setState({ listKeyPair: Array.isArray(res.data) ? res.data : [], isLoading: false });
            })
            .catch(() => {
                this.setState({ isLoading: false });
            });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    /* APIKeyEditable 위임 핸들러 */
    handleOpenAddModal = () => {
        if (this.apiKeyRef.current) this.apiKeyRef.current.openAddModal();
    };

    handleEdit = (row) => {
        if (this.apiKeyRef.current) this.apiKeyRef.current.startEdit(row);
    };

    handleDelete = (rowId) => {
        if (this.apiKeyRef.current) this.apiKeyRef.current.confirmDelete(rowId);
    };

    handleShowSecret = (rowId) => {
        if (!this.apiKeyRef.current) return;
        /* 이미 공개 상태면 토글로 마스킹 복귀 */
        if (this.state.revealedSecrets[rowId]) {
            this.setState((prev) => {
                const next = { ...prev.revealedSecrets };
                delete next[rowId];
                return { revealedSecrets: next };
            });
            return;
        }
        /* fetchSecrets 호출 후 내부 state.secretData 에서 polling 으로 읽어와 V3 state 에 복사 */
        this.apiKeyRef.current.fetchSecrets(rowId);
        const pollSecrets = (retries = 20) => {
            const inst = this.apiKeyRef.current;
            if (!inst) return;
            const secretData = inst.state.secretData;
            if (secretData && secretData[rowId]) {
                this.setState((prev) => ({
                    revealedSecrets: { ...prev.revealedSecrets, [rowId]: secretData[rowId] },
                }));
                return;
            }
            if (retries <= 0) return;
            setTimeout(() => pollSecrets(retries - 1), 100);
        };
        pollSecrets();
    };

    render() {
        const { collapsed, listKeyPair, isLoading, isMobile, revealedSecrets } = this.state;

        const sharedProps = {
            keyPairs: listKeyPair,
            isLoading,
            onOpenAddModal: this.handleOpenAddModal,
            onEdit: this.handleEdit,
            onDelete: this.handleDelete,
            onShowSecret: this.handleShowSecret,
            revealedSecrets,
            collapsed,
            onCollapse: this.onCollapse,
        };

        return (
            <>
                {isMobile
                    ? <MyPageMobileV2 {...sharedProps} />
                    : <MyPageDesktopV2 {...sharedProps} />
                }
                {/*
                 * APIKeyEditable을 hideListRender 모드로 마운트.
                 * 리스트/테이블은 V3 카드 UI가 직접 렌더하고,
                 * Add/Edit/Delete/Secret 모달 + 알림 토스트만 이 인스턴스가 렌더.
                 * display:none 래퍼 없음 — fixed 모달이 실제로 표시되도록 보장 (DAS-66 C-1).
                 */}
                <APIKeyEditable
                    ref={this.apiKeyRef}
                    hideListRender
                    onDataChange={this.loadKeyPairs}
                />
            </>
        );
    }
}

export default MyPageV3;
